import { createContext, useCallback, useContext, useMemo, useState, type PropsWithChildren } from "react";
import { api, jeton } from "@shared/lib/apiClient";
import type { RoleUtilisateur } from "@sfs/contracts";

type Utilisateur = { id: string; email: string; nom: string; role: RoleUtilisateur };

type Contexte = {
  utilisateur: Utilisateur | null;
  connexion: (email: string, motDePasse: string) => Promise<void>;
  deconnexion: () => void;
};

const Ctx = createContext<Contexte | null>(null);
const CLE = "sfs.utilisateur";

export function AuthProvider({ children }: PropsWithChildren) {
  const [utilisateur, setUtilisateur] = useState<Utilisateur | null>(() => {
    const brut = localStorage.getItem(CLE);
    return brut ? (JSON.parse(brut) as Utilisateur) : null;
  });

  const connexion = useCallback(async (email: string, motDePasse: string) => {
    const s = await api.post<{ jeton: string; utilisateur: Utilisateur }>("/auth/connexion", { email, motDePasse });
    jeton.ecrire(s.jeton);
    localStorage.setItem(CLE, JSON.stringify(s.utilisateur));
    setUtilisateur(s.utilisateur);
  }, []);

  const deconnexion = useCallback(() => {
    jeton.effacer();
    localStorage.removeItem(CLE);
    setUtilisateur(null);
    window.location.href = "/connexion";
  }, []);

  const valeur = useMemo(() => ({ utilisateur, connexion, deconnexion }), [utilisateur, connexion, deconnexion]);
  return <Ctx.Provider value={valeur}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const c = useContext(Ctx);
  if (!c) throw new Error("useAuth doit être utilisé dans AuthProvider");
  return c;
}
